import React, { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api, { apiError } from "@/lib/api";
import { uploadAsset, mediaUrl } from "@/lib/upload";
import { PageHeader, Panel, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Camera, FloppyDisk, Moon, Sun, User as UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CompanyProfileCard } from "@/components/CompanyProfileCard";

const ROLE_LABELS = { admin: "Administrator", sales: "Sales Agent", dispatcher: "Dispatcher", driver: "Driver" };

export default function Settings() {
  const { user, refresh } = useAuth();
  const { dark, setDark } = useTheme();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "", title: user?.title || "", direct_line: user?.direct_line || "",
    location: user?.location || "", bio: user?.bio || "",
  });
  const [avatar, setAvatar] = useState(user?.avatar_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAsset(file, "avatars");
      setAvatar(res.url);
      await api.put("/profile", { avatar_url: res.url });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { ...form });
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleDark = async (v) => {
    setDark(v);
    try { await api.put("/profile", { dark_mode: v }); } catch (e) { console.error("Failed to save theme preference", e); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Settings" subtitle="Manage your profile, preferences and appearance" />

      <div className="space-y-5">
        <Panel className="p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-5">Profile</h3>
          <div className="flex items-center gap-5 mb-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-blue-100 overflow-hidden flex items-center justify-center text-2xl font-heading font-extrabold text-blue-700 ring-4 ring-blue-50">
                {avatar ? <img src={mediaUrl(avatar)} alt="avatar" className="w-full h-full object-cover" data-testid="settings-avatar-img" /> : (user.name?.[0]?.toUpperCase() || <UserIcon size={28} />)}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="settings-avatar-btn"
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-blue-600 text-white grid place-items-center shadow-md hover:bg-blue-700 transition-colors"
                title="Change photo"
              >
                <Camera size={16} weight="fill" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="settings-avatar-input" />
            </div>
            <div>
              <p className="font-heading font-bold text-slate-900 text-lg">{user.name}</p>
              <p className="text-sm text-slate-500">{user.email}</p>
              <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{ROLE_LABELS[user.role]}</span>
              {uploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name"><Input data-testid="settings-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white border-slate-300" /></Field>
            <Field label="Phone"><Input data-testid="settings-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white border-slate-300" /></Field>
            <Field label="Position / Job Title"><Input data-testid="settings-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-white border-slate-300" placeholder="e.g. Senior Dispatcher" /></Field>
            <Field label="Direct Line (shown to drivers & carriers)"><Input data-testid="settings-direct-line" value={form.direct_line} onChange={(e) => set("direct_line", e.target.value)} className="bg-white border-slate-300" placeholder="+1 (555) 000-0000" /></Field>
            <Field label="Location"><Input data-testid="settings-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-white border-slate-300" placeholder="City, State" /></Field>
            <div className="sm:col-span-2"><Field label="About"><Textarea data-testid="settings-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} className="bg-white border-slate-300" rows={3} placeholder="A Short Bio…" /></Field></div>
          </div>

          <div className="flex justify-end mt-5">
            <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="settings-save-btn">
              <FloppyDisk size={17} className="mr-1.5" weight="fill" />{saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </Panel>

        <Panel className="p-6">
          <h3 className="font-heading font-bold text-slate-900 mb-1">Appearance</h3>
          <p className="text-sm text-slate-500 mb-4">Choose how Asterism Services looks to you.</p>
          <div className="flex gap-3">
            <button
              onClick={() => toggleDark(false)}
              data-testid="theme-light-btn"
              className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${!dark ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <Sun size={22} weight="duotone" className="text-amber-500" />
              <div className="text-left"><p className="font-semibold text-slate-900">Light</p><p className="text-xs text-slate-500">Bright & Clean</p></div>
            </button>
            <button
              onClick={() => toggleDark(true)}
              data-testid="theme-dark-btn"
              className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${dark ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <Moon size={22} weight="duotone" className="text-indigo-500" />
              <div className="text-left"><p className="font-semibold text-slate-900">Dark</p><p className="text-xs text-slate-500">Easy on the Eyes</p></div>
            </button>
          </div>
        </Panel>
        {user.role === "admin" && <CompanyProfileCard />}
      </div>
    </div>
  );
}
